import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, FileText, X } from "lucide-react";
import api from "@/lib/api";

const TEMPLATES = [
  {
    key: "template_1",
    name: "NAV Statement",
    subtitle: "Account Summary, Shares & Closing NAV",
    description: "Account Summary (MTD/YTD), Shares Summary, Closing NAV. Classic navy & white professional layout.",
    colors: ["#1a1a3e", "#2a3f6f", "#f0f0f0", "#ffffff"],
    accent: "#1a1a3e",
    fundType: "Open Ended",
  },
  {
    key: "template_2",
    name: "Equity Statement",
    subtitle: "Capital Flows, Fees & Net Return",
    description: "Capital Additions/Withdrawals, Fee Allocations, Net Return. Modern teal & charcoal design.",
    colors: ["#0d7377", "#14a3a8", "#f7fafa", "#1e1e2f"],
    accent: "#0d7377",
    fundType: "Open Ended",
  },
  {
    key: "template_3",
    name: "Partner Capital",
    subtitle: "Commitment, MOIC & Operating Activity",
    description: "Capital Commitment, MOIC, Operating Activity (QTD/YTD/Inception). Minimal grayscale styling.",
    colors: ["#111111", "#555555", "#eeeeee", "#ffffff"],
    accent: "#111111",
    fundType: "Closed Ended",
  },
];

export default function StatementTemplatesPage() {
  const [loading, setLoading] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState("");

  const handlePreview = async (templateKey) => {
    setLoading(templateKey);
    try {
      const res = await api.get(`/statement-template-preview/${templateKey}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setPreviewName(TEMPLATES.find(t => t.key === templateKey)?.name || templateKey);
      };
      reader.readAsDataURL(blob);
    } catch {
      /* silently fail */
    } finally {
      setLoading(null);
    }
  };

  const closePreview = () => {
    setPreviewUrl(null);
    setPreviewName("");
  };

  return (
    <div className="space-y-6" data-testid="statement-templates-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" data-testid="templates-heading">
          Investor Statement Templates
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Preview the available PDF templates. The template is selected per fund during creation.
        </p>
      </div>

      <div className="grid gap-5 md:grid-cols-3">
        {TEMPLATES.map((t) => (
          <Card
            key={t.key}
            className="overflow-hidden border border-border/60 hover:border-primary/40 transition-colors"
            data-testid={`template-card-${t.key}`}
          >
            <div className="h-2" style={{ background: `linear-gradient(90deg, ${t.colors[0]}, ${t.colors[1]})` }} />
            <div className="mx-4 mt-4 rounded-md border border-border/40 bg-muted/30 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded" style={{ background: t.accent }} />
                  <div className="space-y-1">
                    <div className="h-2 w-24 rounded-full" style={{ background: t.accent, opacity: 0.7 }} />
                    <div className="h-1.5 w-16 rounded-full bg-muted-foreground/20" />
                  </div>
                </div>
                <div className="space-y-1 text-right">
                  <div className="h-1.5 w-20 rounded-full bg-muted-foreground/20 ml-auto" />
                  <div className="h-1.5 w-14 rounded-full bg-muted-foreground/15 ml-auto" />
                </div>
              </div>
              <div className="h-px w-full" style={{ background: t.accent, opacity: 0.4 }} />
              <div className="space-y-1.5 pt-1">
                <div className="h-4 w-full rounded" style={{ background: t.accent }} />
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex gap-2">
                    <div className="h-3 flex-1 rounded-sm bg-muted-foreground/10" />
                    <div className="h-3 w-16 rounded-sm bg-muted-foreground/15" />
                  </div>
                ))}
                <div className="flex gap-2">
                  <div className="h-3 flex-1 rounded-sm" style={{ background: t.accent, opacity: 0.15 }} />
                  <div className="h-3 w-16 rounded-sm" style={{ background: t.accent, opacity: 0.2 }} />
                </div>
              </div>
            </div>

            <CardContent className="pt-4 pb-4 space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold text-sm" data-testid={`template-name-${t.key}`}>{t.name}</h3>
                <span className="text-xs text-muted-foreground">— {t.subtitle}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-secondary border border-border/50 text-muted-foreground">{t.fundType}</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{t.description}</p>

              <div className="flex items-center gap-1.5">
                {t.colors.map((c, i) => (
                  <div key={i} className="h-4 w-4 rounded-full border border-border/50" style={{ background: c }} />
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2"
                disabled={loading === t.key}
                onClick={() => handlePreview(t.key)}
                data-testid={`preview-btn-${t.key}`}
              >
                {loading === t.key ? (
                  <span className="animate-spin h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
                Preview PDF
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Embedded PDF Viewer Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="bg-card border-border max-w-4xl h-[85vh] flex flex-col p-0" data-testid="pdf-preview-dialog">
          <DialogHeader className="px-6 py-4 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="font-heading text-lg">{previewName} — Preview</DialogTitle>
              <Button variant="ghost" size="icon" onClick={closePreview} data-testid="close-preview-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 p-4 overflow-hidden">
            {previewUrl && (
              <iframe
                src={previewUrl}
                className="w-full h-full rounded-md border border-border bg-white"
                title="PDF Preview"
                data-testid="pdf-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
